// galeria zdjęć - lokalna wersja zgodna z Figma

import React, { FC, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import Slider from 'react-slick';
import { X, ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import { IImage, IProductLabel } from 'api/types';
import { Label } from 'components/controls';

import styles from 'theme/pages/Product/components/Gallery/Gallery.module.scss';

// typ danych wejściowych
interface IProps {
  images: IImage[];
  labels: IProductLabel[];
}

const Gallery: FC<IProps> = ({ images, labels }) => {
  // index aktualnego zdjęcia (z tablicy zdjęć)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const goPrev = useCallback(
    () => setCurrentImageIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const goNext = useCallback(
    () => setCurrentImageIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (!isFullscreenOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreenOpen(false);
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreenOpen, goPrev, goNext]);

  // Ustawienia dla slidera thumbnails
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: images.length > 4 ? 4 : images.length,
    slidesToScroll: 1,
    arrows: images.length > 4,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: images.length > 3 ? 3 : images.length,
          arrows: images.length > 3
        }
      }
    ]
  };

  return (
    <div
      className={classnames(styles.componentWrapper, 'StylePath-Pages-Product-components-Gallery')}>
      {/* Lightbox pełnoekranowy */}
      {isFullscreenOpen && images.length > 0 && (
        <div
          className={styles.fullscreenOverlay}
          onClick={() => setIsFullscreenOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Galeria zdjęć">
          <button
            type="button"
            className={styles.fullscreenClose}
            onClick={() => setIsFullscreenOpen(false)}
            aria-label="Zamknij">
            <X size={32} />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                className={styles.fullscreenPrev}
                onClick={(e) => {
                  e.stopPropagation();
                  goPrev();
                }}
                aria-label="Poprzednie zdjęcie">
                <ChevronLeft size={40} />
              </button>
              <button
                type="button"
                className={styles.fullscreenNext}
                onClick={(e) => {
                  e.stopPropagation();
                  goNext();
                }}
                aria-label="Następne zdjęcie">
                <ChevronRight size={40} />
              </button>
            </>
          )}
          <div
            className={styles.fullscreenImageWrap}
            onClick={(e) => e.stopPropagation()}>
            <img
              src={images[currentImageIndex]?.big || images[currentImageIndex]?.thumb || ''}
              alt=""
              className={styles.fullscreenImage}
            />
          </div>
          {images.length > 1 && (
            <div className={styles.fullscreenCounter}>
              {currentImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}

      <div className={styles.mainImageWrapper}>
        <div
          className={styles.mainImage}
          style={{
            backgroundImage: `url(${images[currentImageIndex]?.big || images[currentImageIndex]?.thumb || ''})`
          }}
          onClick={() => images.length > 0 && setIsFullscreenOpen(true)}
          onKeyDown={(e) => e.key === 'Enter' && images.length > 0 && setIsFullscreenOpen(true)}
          role={images.length > 0 ? 'button' : undefined}
          tabIndex={images.length > 0 ? 0 : undefined}
          aria-label="Powiększ zdjęcie">
          {labels.map((label) => (
            <Label key={label.type} label={label} />
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <div className={styles.thumbnailsWrapper}>
          <Slider {...sliderSettings} className={styles.thumbnailsSlider}>
            {images.map((image, index) => (
              <div key={index} className={styles.thumbContainer}>
                <div
                  className={classnames(styles.thumb, {
                    [styles.active]: index === currentImageIndex
                  })}
                  onClick={() => setCurrentImageIndex(index)}>
                  <div
                    className={styles.thumbImage}
                    style={{ backgroundImage: `url(${image.thumb || image.big || ''})` }}
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default Gallery;


